import { CompanyContactPersonChargeDto, CurrencyIDs } from "Api/Api";
import { CompaniesSideMenu } from "Components/Companies/CompaniesSideMenu";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { BreadcrumbItem } from "Components/Layout/Breadcrumbs";
import {
  Column,
  PrimaryDataTable,
  TableIDType,
} from "Components/Shared/PrimaryDataTable/PrimaryDataTable";
import { StyledTableWrapper } from "Components/Shared/PrimaryDataTable/PrimaryDataTableStyles";
import { PrimarySkeleton } from "Components/Shared/PrimarySkeleton";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRouteMatch } from "react-router-dom";
import { getCompaniesDetailAsync } from "State/Companies/CompanyReducer";
import { RootStateType } from "State/Store";
import styled from "styled-components";
import { Resources, useResource } from "Translations/Resources";
import { formatDate, formatMonthWithYear } from "Utils/DateUtils";
import { formatNumber } from "Utils/NumberUtils";
import { isNoU } from "Utils/ObjectUtils";

const StyledLayout = styled.div`
  ${props => props.theme.breakpoints.down("xs")} {
    h3 {
      padding: ${props => props.theme.spacing(0, 1)};
    }
  }
`;

const StyledNoWrap = styled.div`
  white-space: nowrap;
`;

type RouteProps = {
  id: string;
};

type Props = {};

export const CompanyContactPersonsChargesHistoryPage: React.FunctionComponent<Props> = props => {
  const { params } = useRouteMatch<RouteProps>();
  const companyID = params.id as any;
  const dispatch = useDispatch();
  const { t } = useResource();

  const { companyDetail, isLoading } = useSelector(
    (e: RootStateType) => e.companies,
  );

  React.useEffect(() => {
    dispatch(getCompaniesDetailAsync.request(companyID));
  }, [dispatch, companyID]);

  const getBreadcrumbItems = () => {
    return [
      {
        name: t(Resources.Companies.Title),
        url: t(Resources.Routes.Companies),
      } as BreadcrumbItem,
      {
        name: t(Resources.CompanyEdit.Breadcrumb.Title),
      } as BreadcrumbItem,
    ];
  };

  const getCell = (
    id: TableIDType<CompanyContactPersonChargeDto>,
    dto: CompanyContactPersonChargeDto,
  ) => {
    switch (id) {
      case "companyContactPersonID":
        const person = companyDetail?.contacts.find(
          x => x.companyContactPersonID === dto.companyContactPersonID,
        );
        return (
          <StyledNoWrap>
            {person?.firstName ?? ""} {person?.lastName ?? ""}
          </StyledNoWrap>
        );
      case "dateCreated":
        return <StyledNoWrap>{formatDate(dto.dateCreated, true)}</StyledNoWrap>;

      case "manDayCharge": {
        const currencyUnit = dto.currencyID === CurrencyIDs.EUR ? "€" : "Kč";
        return (
          <StyledNoWrap>
            {formatNumber(dto.manDayCharge)} {currencyUnit}
          </StyledNoWrap>
        );
      }

      case "dateFrom":
        return (
          <StyledNoWrap>{`${formatMonthWithYear(
            dto.dateFrom,
          )} - ${formatMonthWithYear(dto.dateTo)}`}</StyledNoWrap>
        );

      case "createdBy":
        return (
          <StyledNoWrap>
            {dto?.createdByFirstName ?? ""} {dto?.createdByLastName ?? ""}
          </StyledNoWrap>
        );

      default:
        return <div>-</div>;
    }
  };

  const columns: Column<CompanyContactPersonChargeDto>[] = [
    {
      id: "companyContactPersonID",
      label: "Kontaktní osoba",
    },
    {
      id: "dateCreated",
      label: "Datum vytvoření",
    },
    {
      id: "manDayCharge",
      label: "Sazba/MD",
    },
    {
      id: "dateFrom",
      label: "Období",
    },
    {
      id: "createdBy",
      label: "Vytvořil",
    },
  ];

  return (
    <>
      <StyledLayout>
        <AuthenticatedLayout
          title={companyDetail?.name ?? ""}
          hasSideMenu={!isNoU(companyID)}
          breadcrumbItems={getBreadcrumbItems()}
        >
          <CompaniesSideMenu companyID={companyID} />

          {companyDetail && companyDetail.contacts && (
            <div className={"container-small"}>
              <div className={"box"}>
                <div className={"box-header"}>
                  {!isLoading && companyDetail ? (
                    <span>Historie sazeb</span>
                  ) : (
                    <PrimarySkeleton height={30} />
                  )}
                </div>
                <StyledTableWrapper className={"box-content"}>
                  <PrimaryDataTable
                    isLoading={isLoading}
                    rows={companyDetail.contacts
                      .flatMap(x => x.charges)
                      .map(e => ({
                        data: e,
                        rowEntityID: "companyContactPersonChargeID",
                      }))}
                    getHeaderCell={column => <div>{column.label}</div>}
                    getCell={getCell}
                    columns={columns}
                  ></PrimaryDataTable>
                </StyledTableWrapper>
              </div>
            </div>
          )}
        </AuthenticatedLayout>
      </StyledLayout>
    </>
  );
};
